import { useEffect, useState, useCallback } from 'react';

interface Identifiable {
  id: number;
}

interface UseBadgeMultiSelectOptions<T extends Identifiable> {
  loadAllItems: () => Promise<T[]>;
  initialSelected?: T[];
}

export function useBadgeMultiSelect<T extends Identifiable>({
  loadAllItems,
  initialSelected = [],
}: UseBadgeMultiSelectOptions<T>) {
  const [allItems, setAllItems] = useState<T[]>([]);
  const [selectedItems, setSelectedItems] = useState<T[]>(initialSelected);

  useEffect(() => {
    loadAllItems()
      .then(setAllItems)
      .catch((err) => console.error('Failed to load items:', err));
  }, [loadAllItems]);

  const toggleItem = useCallback(
    (item: T) => {
      const exists = selectedItems.some((s) => s.id === item.id);
      const updated = exists
        ? selectedItems.filter((s) => s.id !== item.id)
        : [...selectedItems, item];
      setSelectedItems(updated);
    },
    [selectedItems]
  );

  const selectAllItems = useCallback(() => {
    setSelectedItems(allItems);
  }, [allItems]);

  return {
    allItems,
    selectedItems,
    setSelectedItems,
    toggleItem,
    selectAllItems,
  };
}
